import type { FacilityNode } from "@arc/contracts";

/**
 * Pure tree helpers — no React, no I/O, fully unit-tested. The API returns a flat, path-ordered list (a rooted forest,
 * ancestors included); the UI needs it nested, plus instant client-side search. Keeping this a pure module is the
 * "testable core" (Builder/transform): the components stay thin around it.
 */

export interface FacilityTreeNode extends FacilityNode {
  children: FacilityTreeNode[];
  /** Direct children present in the permitted set (the "subfacilities" count shown on the row). */
  childCount: number;
}

/** Alphabetical order for facility siblings — locale-aware, case-insensitive, and NATURAL-numeric so "Line 2" sorts
 *  before "Line 10" (not lexicographically after). The single ordering used at EVERY level by both navigators + the
 *  move picker (all build their levels through `buildForest`). The tree is unordered in the DB (no order key), so name
 *  is the stable, user-meaningful sort. */
export function compareFacilityName(a: FacilityNode, b: FacilityNode): number {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Build the nested forest from the flat list via `parentId`. O(n log n) — roots and every node's children are sorted
 * **A-Z by name** ({@link compareFacilityName}) so the browse order is alphabetical at every level regardless of the
 * API's path order. A node whose parent is absent becomes a root.
 */
export function buildForest(flat: readonly FacilityNode[]): FacilityTreeNode[] {
  const byId = new Map<number, FacilityTreeNode>();
  for (const n of flat) byId.set(n.id, { ...n, children: [], childCount: 0 });
  const roots: FacilityTreeNode[] = [];
  for (const n of flat) {
    const node = byId.get(n.id)!;
    const parent = n.parentId !== null ? byId.get(n.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  for (const node of byId.values()) {
    node.children.sort(compareFacilityName);
    node.childCount = node.children.length;
  }
  roots.sort(compareFacilityName);
  return roots;
}

/**
 * Build the forest of ARCHIVED facilities for the Archived view. Each archived node nests under its NEAREST ARCHIVED
 * ancestor — skipping any ACTIVE intermediate in a mixed `archived → active → archived` chain (a state migrated data
 * can hold; see {@link excludeArchived}'s mirror relink for the active view). So the forest ROOTS are exactly the
 * archived-subtree roots ({@link archivedSubtreeRootIds}) and every archived node nests under a restorable ancestor —
 * a plain `buildForest(archived-only)` would instead promote such a node to a detached top-level row with no Unarchive.
 */
export function buildArchivedForest(flat: readonly FacilityNode[]): FacilityTreeNode[] {
  const byId = new Map(flat.map((f) => [f.id, f] as const));
  const nearestArchivedAncestorId = (n: FacilityNode): number | null => {
    let pid = n.parentId;
    while (pid !== null) {
      const p = byId.get(pid);
      if (!p) return null; // ancestor not in the permitted set → treat as a root here
      if (p.archived) return p.id;
      pid = p.parentId;
    }
    return null;
  };
  const nodes = new Map<number, FacilityTreeNode>();
  for (const f of flat) if (f.archived) nodes.set(f.id, { ...f, children: [], childCount: 0 });
  const roots: FacilityTreeNode[] = [];
  for (const node of nodes.values()) {
    const parentId = nearestArchivedAncestorId(node);
    const parent = parentId !== null ? nodes.get(parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  for (const node of nodes.values()) {
    node.children.sort(compareFacilityName);
    node.childCount = node.children.length;
  }
  roots.sort(compareFacilityName);
  return roots;
}

export interface SearchResult {
  /** Nodes whose name matches the query (empty when the query is blank). */
  matchIds: Set<number>;
  /** Nodes to render: the matches + all their ancestors (the path to each match). All ids when the query is blank. */
  visibleIds: Set<number>;
  /** Ancestors of matches — forced open so every match is reachable. Empty when the query is blank. */
  expandedIds: Set<number>;
}

/**
 * Case-insensitive name search over the flat list. A match pulls in its ancestor chain (so the branch stays rooted and
 * visible) and force-expands those ancestors. Blank query ⇒ everything visible, nothing force-expanded (the user's own
 * expand state governs).
 */
export function searchTree(flat: readonly FacilityNode[], query: string): SearchResult {
  const q = query.trim().toLowerCase();
  if (!q) {
    return { matchIds: new Set(), visibleIds: new Set(flat.map((f) => f.id)), expandedIds: new Set() };
  }
  const matchIds = new Set<number>();
  for (const f of flat) if (f.name.toLowerCase().includes(q)) matchIds.add(f.id);
  return { matchIds, ...expandToMatches(flat, matchIds) };
}

/**
 * Given a set of "match" ids, return the nodes to render (each match + its ancestor chain) and the ancestors to
 * force-open. Shared by search and the Favorites filter — both are "show these nodes, keep them reachable".
 */
export function expandToMatches(
  flat: readonly FacilityNode[],
  matchIds: ReadonlySet<number>,
): { visibleIds: Set<number>; expandedIds: Set<number> } {
  const byId = new Map(flat.map((f) => [f.id, f] as const));
  const visibleIds = new Set<number>();
  const expandedIds = new Set<number>();
  for (const id of matchIds) {
    let cur = byId.get(id);
    while (cur) {
      visibleIds.add(cur.id);
      if (cur.parentId === null) break;
      expandedIds.add(cur.parentId);
      cur = byId.get(cur.parentId);
    }
  }
  return { visibleIds, expandedIds };
}

export interface FlatRow {
  node: FacilityTreeNode;
  /** Render depth (0 at the visible roots) — drives indentation, not the absolute ltree nlevel. */
  depth: number;
  expanded: boolean;
  hasChildren: boolean;
}

/**
 * Depth-first flatten of the forest into the ordered list of ROWS to render, honoring expand + optional visibility
 * filters. This is what the a11y tree renders (a flat list of `treeitem`s with `aria-level`), and what arrow-key
 * navigation steps through.
 */
export function flattenVisible(
  forest: readonly FacilityTreeNode[],
  opts: { isExpanded: (id: number) => boolean; isVisible?: (id: number) => boolean },
): FlatRow[] {
  const rows: FlatRow[] = [];
  const walk = (nodes: readonly FacilityTreeNode[], depth: number): void => {
    for (const n of nodes) {
      if (opts.isVisible && !opts.isVisible(n.id)) continue;
      // hasChildren must reflect the FILTERED view: a matched parent whose children are all filtered out is a leaf
      // here — otherwise it advertises an expand that reveals nothing (ARIA violation + dead toggle). Panel finding.
      const visibleChildren = opts.isVisible ? n.children.filter((c) => opts.isVisible!(c.id)) : n.children;
      const hasChildren = visibleChildren.length > 0;
      const expanded = hasChildren && opts.isExpanded(n.id);
      rows.push({ node: n, depth, expanded, hasChildren });
      if (expanded) walk(visibleChildren, depth + 1);
    }
  };
  walk(forest, 0);
  return rows;
}

/** Toggle an id's membership in a Set immutably (for React state). */
export function toggle(set: ReadonlySet<number>, id: number): Set<number> {
  const next = new Set(set);
  if (!next.delete(id)) next.add(id);
  return next;
}

// ── Move (P6.2) — BEST-EFFORT client mirrors of the server's move authz (ADR-005). The SERVER is authoritative; these
// only hide affordances the client can already tell will fail, so the UI doesn't advertise obviously-doomed drops. A
// residual 403/400/409 is still possible (the client can't see the bypass tier's HPFP nor descendants it can't view)
// and is surfaced in the move dialog. Pure + unit-tested.

/** The caller can act as `manage` on a node (⇒ movable / a valid drop target for a non-bypass caller). */
export function isManageable(node: FacilityNode): boolean {
  return node.permission === "manage" || node.permission === "access_manage";
}

/** ltree descendant-or-equal on the text path: is `a` the same node as `b`, or inside `b`'s subtree? Mirrors `a <@ b`. */
export function isWithin(a: string, b: string): boolean {
  return a === b || a.startsWith(`${b}.`);
}

/**
 * Bypass-tier hint: an effective `access_manage` on ANY node means the caller's role HPFP is `access_manage` (effective =
 * min(grant, HPFP)), i.e. the bypass tier (a superadmin sees every node as `access_manage`). Conservative — a rare
 * HPFP=access_manage caller holding no `access_manage` grant reads as non-bypass; the server would still allow them.
 */
export function bypassHint(flat: readonly FacilityNode[]): boolean {
  return flat.some((n) => n.permission === "access_manage");
}

/**
 * Can `source` be dropped onto `target` as its new parent? Blocks self + own-subtree (a cycle — 400 even for bypass);
 * for a non-bypass caller also requires manage on both. `target` is the destination PARENT node.
 */
export function canDrop(source: FacilityNode, target: FacilityNode, bypass: boolean): boolean {
  if (target.id === source.id) return false;
  if (isWithin(target.path, source.path)) return false; // target is the source or inside its subtree → cycle
  if (bypass) return true;
  return isManageable(source) && isManageable(target);
}

/**
 * Can the caller move `source` at all (⇒ show its drag handle + "Move to…")? Mirrors the server's SOURCE-side check
 * over the data the client has: manage on the node + every descendant present + the origin parent. A root source
 * (no origin parent) or any move is allowed outright for the bypass tier.
 */
export function canMoveSource(source: FacilityNode, flat: readonly FacilityNode[], bypass: boolean): boolean {
  if (bypass) return true;
  if (source.parentId === null) return false; // moving FROM root is bypass-only
  const originParent = flat.find((f) => f.id === source.parentId);
  if (!originParent || !isManageable(originParent)) return false;
  // source + every descendant present in the tree must be manageable (all-or-nothing, like the server)
  return flat.filter((f) => isWithin(f.path, source.path)).every(isManageable);
}

/**
 * Can the caller archive/unarchive `node` (⇒ show the Archive kebab item / the Unarchive button)? Best-effort mirror
 * of the server's `requireManageSubtree` over the data the client has: bypass, OR `manage` on the node AND every
 * descendant present in the tree (all-or-nothing). The server enforces authoritatively (a descendant the caller can't
 * see would still 403). Unlike `canMoveSource` there's no origin-parent / no root restriction.
 */
export function canManageSubtree(node: FacilityNode, flat: readonly FacilityNode[], bypass: boolean): boolean {
  if (bypass) return true;
  return flat.filter((f) => isWithin(f.path, node.path)).every(isManageable);
}

/**
 * The ACTIVE (non-archived) nodes for the All/Favorites views, with each node's `parentId` re-linked to its nearest
 * NON-archived ancestor (or null). This HOISTS an active descendant of a hidden archived node to the nearest visible
 * ancestor instead of letting it vanish when archived nodes are excluded (panel): a plain visibility filter would skip
 * a hidden node AND its whole subtree. Paths are left unchanged (nesting uses the re-linked `parentId`). Going forward
 * the server's unarchive ancestor-guard prevents new active-under-archived states; this covers pre-existing/ETL data.
 */
/**
 * The archived-subtree ROOT ids — archived nodes whose (transitive) ancestors are all active. Archive/unarchive
 * cascade the whole subtree AND the server rejects unarchiving a node that still has an archived ancestor
 * (`FACILITY_ANCESTOR_ARCHIVED`), so ONLY these tops carry the Unarchive action — their archived descendants are
 * restored together with them. An archived node whose parent is also archived returns false (no button on it).
 */
export function archivedSubtreeRootIds(flat: readonly FacilityNode[]): Set<number> {
  const byId = new Map(flat.map((f) => [f.id, f] as const));
  const underArchived = (n: FacilityNode): boolean => {
    let pid = n.parentId;
    while (pid !== null) {
      const p = byId.get(pid);
      if (!p) return false; // ancestor not in the permitted set → treat as a top here
      if (p.archived) return true;
      pid = p.parentId;
    }
    return false;
  };
  return new Set(flat.filter((f) => f.archived && !underArchived(f)).map((f) => f.id));
}

export function excludeArchived(flat: readonly FacilityNode[]): FacilityNode[] {
  const byId = new Map(flat.map((f) => [f.id, f] as const));
  const nearestActiveParentId = (n: FacilityNode): number | null => {
    let pid = n.parentId;
    while (pid !== null) {
      const p = byId.get(pid);
      if (!p) return null; // ancestor not in the permitted set → treat as a root here
      if (!p.archived) return p.id; // nearest non-archived ancestor
      pid = p.parentId;
    }
    return null;
  };
  return flat.filter((f) => !f.archived).map((f) => ({ ...f, parentId: nearestActiveParentId(f) }));
}

/**
 * "Root / Parent" — the ancestor names of `id`, top-down, joined with " / " (empty for a root, or for an id the map does
 * not know). Resolves over whatever map the caller holds (the FULL tree for a breadcrumb over a flat list). Shared by
 * the drill-down's cards and the workspace's QR page (ADR-032).
 */
export function facilityPathLabel(byId: ReadonlyMap<number, { name: string; parentId: number | null }>, id: number): string {
  const names: string[] = [];
  let pid = byId.get(id)?.parentId ?? null;
  while (pid !== null) {
    const p = byId.get(pid);
    if (!p) break;
    names.unshift(p.name);
    pid = p.parentId;
  }
  return names.join(" / ");
}
